import { Injectable } from '@angular/core';
import { 
  HttpInterceptor, 
  HttpRequest, 
  HttpEvent, 
  HttpHandler, 
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { KeyCloakCustomService } from '../../keycloak/services/key-cloak-custom-service';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(private keycloakService: KeyCloakCustomService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip auth for certain URLs
    if (this.shouldSkip(req)) {
      return next.handle(req);
    }

    const token = this.keycloakService.token;
    
    if (!token) {
      return next.handle(req);
    }

    // Add token to request
    const authReq = req.clone({
      setHeaders: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Try to refresh token and retry
          return from(this.keycloakService.refreshToken()).pipe(
            switchMap((success: boolean) => {
              if (success && this.keycloakService.token) {
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${this.keycloakService.token}` }
                });
                return next.handle(retryReq);
              }
              return throwError(() => error);
            }),
            catchError(() => throwError(() => error))
          );
        }
        return throwError(() => error);
      })
    );
  }

  private shouldSkip(req: HttpRequest<any>): boolean {
    // Skip token check for these specific paths
    const skipUrls = [
      '/protocol/openid-connect/token',
      '/protocol/openid-connect/revoke',
      'assets/',
      'i18n/',
      '.json' // Skip any JSON files
    ];
    
    // Check if the URL matches any of the skip patterns
    const shouldSkip = skipUrls.some(url => req.url.includes(url));
    
    // Only skip if it's a URL we explicitly want to skip
    // and it's not an API call that needs authentication
    const isApiCall = req.url.includes(environment.baseApiUrl) || 
                     req.url.includes(environment.awsBaseUrl);
    
    return shouldSkip && !isApiCall;
  }
}